import html2canvas from 'html2canvas';

import { log } from '../../util/log';
import { POWER_BI_EMBED_CLASS } from '../app/embedded-content';

const SCREEN_CAPTURE_ATTRIBUTE = 'syntiro-screen-capture';
const SCREEN_CAPTURE_EXCLUDE_VALUE = 'exclude';

const LOADING_INDICATOR_SELECTORS = [
  '.MuiLinearProgress-root',
  '.MuiSkeleton-root',
  '.MuiDataGrid-loadingOverlay'
];

export type ImageMimeType = 'image/webp';

const DEFAULT_IMAGE_ENCODE_QUALITY = 0.85;

export type ScreenCaptureResult =
  | {
      status: 'ok';
      canvas: HTMLCanvasElement;
      widthPx: number;
      heightPx: number;
      durationMs: number;
    }
  | { status: 'loading' }
  | { status: 'failed'; reason: string };

/**
 * Returns true if `element` (and therefore html2canvas's cloned subtree rooted at it) should be
 * skipped entirely during capture. Checked directly against the element, never via `closest`,
 * because html2canvas's `ignoreElements` already skips the whole subtree once this returns true
 * for an ancestor.
 * @param {Element} element Element being considered by html2canvas's `ignoreElements` predicate.
 * @returns {boolean} Whether to exclude this element and its descendants from the capture.
 */
export function shouldIgnoreElementForCapture(element: Element): boolean {
  if (element.getAttribute(SCREEN_CAPTURE_ATTRIBUTE) === SCREEN_CAPTURE_EXCLUDE_VALUE) {
    return true;
  }
  return element.classList.contains(POWER_BI_EMBED_CLASS);
}

/**
 * Returns true if a loading indicator is currently visible outside of any
 * capture-excluded subtree.
 * @returns {boolean} Whether a real, non-excluded loading indicator is present in the document.
 */
export function isVisibleLoadingIndicatorInCanvas(): boolean {
  return LOADING_INDICATOR_SELECTORS.some((selector) => {
    const matchingElements = document.querySelectorAll(selector);
    return Array.from(matchingElements).some(
      (element) =>
        element.closest(`[${SCREEN_CAPTURE_ATTRIBUTE}="${SCREEN_CAPTURE_EXCLUDE_VALUE}"]`) === null
    );
  });
}

/**
 * Captures a full-document raster of the current screen via html2canvas. Never throws — every
 * failure mode is reported through the returned result.
 * @returns {Promise<ScreenCaptureResult>}
 */
export async function captureScreenRaster(): Promise<ScreenCaptureResult> {
  if (isVisibleLoadingIndicatorInCanvas()) {
    return { status: 'loading' };
  }
  const currentScrollBehavior = document.documentElement.style.scrollBehavior;
  try {
    // Prevents `scroll-behavior: smooth` (src/index.css) from animating html2canvas's internal
    // scroll restore.
    document.documentElement.style.scrollBehavior = 'auto';
    const startTime = performance.now();
    const canvas = await html2canvas(document.body, {
      scrollX: 0,
      scrollY: 0,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: false,
      imageTimeout: 2000,
      logging: false,
      scale: 1,
      ignoreElements: shouldIgnoreElementForCapture
    });
    const durationMs = performance.now() - startTime;

    return { status: 'ok', canvas, widthPx: canvas.width, heightPx: canvas.height, durationMs };
  } catch (err: unknown) {
    const reason = err instanceof Error ? err.message : 'Unknown error during screen capture.';
    log('error', new Error(`captureScreenRaster failed: ${reason}`));
    return { status: 'failed', reason };
  } finally {
    document.documentElement.style.scrollBehavior = currentScrollBehavior;
  }
}

/**
 * Encodes `canvas` to a `Blob` of the given format and quality.
 * @param {HTMLCanvasElement} canvas Canvas to encode.
 * @param {ImageMimeType} [format] MIME type to encode to. Defaults to `'image/webp'`.
 * @param {number} [quality] Encoder quality, where applicable. Defaults to
 *   `DEFAULT_IMAGE_ENCODE_QUALITY`.
 * @returns {Promise<Blob | null>} The encoded blob, or `null` if encoding produced nothing.
 */
async function canvasToImageBlob(
  canvas: HTMLCanvasElement,
  format: ImageMimeType = 'image/webp',
  quality: number = DEFAULT_IMAGE_ENCODE_QUALITY
): Promise<Blob | null> {
  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), format, quality);
  });
}

/**
 * Reads `blob` and returns its contents as a raw base64 string, with no `data:` URI prefix.
 * @param {Blob} blob Blob to encode.
 * @returns {Promise<string>} The base64-encoded contents of `blob`.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const dataUrl = fileReader.result as string;
      const base64Payload = dataUrl.slice(dataUrl.indexOf(',') + 1);
      resolve(base64Payload);
    };
    fileReader.onerror = () =>
      reject(fileReader.error ?? new Error('Failed to read blob as base64.'));
    fileReader.readAsDataURL(blob);
  });
}

export interface EncodedCanvasImage {
  dataBase64: string;
  widthPx: number;
  heightPx: number;
}

/**
 * Encodes `canvas` as an image, base64-encoded with no `data:` URI prefix.
 * Returns `undefined` — never throws — when the browser fell back to a different image type than
 * requested, or when the encoded blob exceeds `maxBytes`.
 * @param {HTMLCanvasElement} canvas Canvas produced by html2canvas.
 * @param {number} maxBytes Caller-supplied hard cap on the encoded blob size.
 * @param {ImageMimeType} [format] MIME type to encode to. Defaults to `'image/webp'`.
 * @param {number} [quality] Encoder quality, where applicable. Defaults to
 *   `DEFAULT_IMAGE_ENCODE_QUALITY`.
 * @returns {Promise<EncodedCanvasImage | undefined>}
 */
export async function encodeCanvasImageToBase64(
  canvas: HTMLCanvasElement,
  maxBytes: number,
  format: ImageMimeType = 'image/webp',
  quality: number = DEFAULT_IMAGE_ENCODE_QUALITY
): Promise<EncodedCanvasImage | undefined> {
  const imageBlob = await canvasToImageBlob(canvas, format, quality);
  if (!imageBlob || imageBlob.type !== format) {
    return undefined;
  }
  if (imageBlob.size > maxBytes) {
    return undefined;
  }
  const dataBase64 = await blobToBase64(imageBlob);
  return { dataBase64, widthPx: canvas.width, heightPx: canvas.height };
}
